import React, { type FC } from 'react';
import { Subscribe } from 'unstated';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { usePageRecommendationData } from '@confluence/page-recommendations/entry-points/usePageRecommendationData.tsx';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

export const VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID = 'view-page-main-content-container';

const styles = cssMap({
	container: {
		display: 'flow-root',
	},
});

type ViewPageMainContentContainerProps = {
	children?: React.ReactNode;
	isFabricPage?: boolean;
	isEmbeddedPage?: boolean;
};

export const ViewPageMainContentContainer: FC<ViewPageMainContentContainerProps> = ({
	children,
	isFabricPage,
	isEmbeddedPage,
}) => {
	// Set minHeight for all Page content above footer comments, so that footer comments
	// are always placed at the bottom of viewport when Page content is short.
	// This CSS-based method is compatible with SSR. So prefer this over dynamically
	// calculating spacing using window.innerHeight, which is not available in SSR.

	// If page has EOP recommendations, don't add any spacing between it and footer
	// comments. So don't set minHeight.
	// If page is a legacy TinyMCE page (i.e. !isFabricPage), don't add spacing either.
	// If page is within an embedded confluence iframe, don't set minHeight to avoid infinite height growth.
	const { hasRecs } = usePageRecommendationData();
	const setMinHeight =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning -- multiple fgs are used in this expression
		isFabricPage &&
		!hasRecs &&
		!isEmbeddedPage &&
		fg('confluence_frontend_content_wrapper') &&
		(fg('confluence_frontend_unify_footer_comment_spacing') ||
			expValEquals('confluence_unify_footer_comments_space_width', 'isEnabled', true));

	// When the experiment is enabled, always render the same component tree
	// (Subscribe > Box) regardless of setMinHeight. This prevents a full DOM remount
	// when hasRecs changes between SSR/SPA
	if (expVal('cc_view_page_stable_content_tree', 'isEnabled', false)) {
		return (
			<Subscribe to={[BannerStateContainer]}>
				{(bannerState: BannerStateContainer) => {
					const bannerHeight = bannerState.getTotalHeight();
					const contentContainerPaddingTop = 46;
					const addFooterCommentHeight = 64;
					const extraFooterCommentHeightToShow = 14;
					return (
						<Box
							xcss={setMinHeight ? styles.container : undefined}
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- minHeight is dynamic, calculated from runtime bannerHeight
								minHeight: setMinHeight
									? `calc(100vh - ${bannerHeight}px - ${contentContainerPaddingTop}px - ${addFooterCommentHeight}px - ${extraFooterCommentHeightToShow}px)`
									: undefined,
							}}
							testId={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
							data-vc={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
						>
							{children}
						</Box>
					);
				}}
			</Subscribe>
		);
	}

	if (setMinHeight) {
		return (
			<Subscribe to={[BannerStateContainer]}>
				{(bannerState: BannerStateContainer) => {
					// - bannerHeight includes top nav bar and any banner above it.
					// - ContentContainerComponent has a 46px padding top when fg('confluence_frontend_content_wrapper') is ON.
					// - ObjectAddCommentContainer has a 64px height.
					// - We lift footer comment top by 14px to make it visually distinct
					//   when a short Page has some vs zero existing footer comment.
					//   (This matches the spacing in LiveDoc.)
					const bannerHeight = bannerState.getTotalHeight();
					const contentContainerPaddingTop = 46;
					const addFooterCommentHeight = 64;
					const extraFooterCommentHeightToShow = 14;
					return (
						<Box
							xcss={styles.container}
							style={{
								minHeight: `calc(100vh - ${bannerHeight}px - ${contentContainerPaddingTop}px - ${addFooterCommentHeight}px - ${extraFooterCommentHeightToShow}px)`,
							}}
							testId={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
							data-vc={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
						>
							{children}
						</Box>
					);
				}}
			</Subscribe>
		);
	}

	return (
		<Box
			testId={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
			data-vc={VIEW_PAGE_MAIN_CONTENT_CONTAINER_TEST_ID}
		>
			{children}
		</Box>
	);
};
